import React, { useCallback, useEffect, useMemo } from 'react';
import c from 'classnames';
import {

  motion,
  AnimatePresence,
  MotionConfig,
  type HTMLMotionProps,

} from 'framer-motion';
import { useTranslation } from 'react-i18next';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { FormRadioButtons } from '@uncinc/uncinc-react-kitchen-sink';

import { useWebformContext } from '../../../context/WebformContext';
import InformationOverlay from '../../InformationOverlay';
import type { WebformRadiosWithImage as Type } from '../types';
import Image from './Image';
import WebformAssessmentCount from '../Assessment/Count';

import './style.scss';

// Type assertion needed for framer-motion compatibility with newer React types.
const MotionDiv = motion.div as unknown as React.FC<HTMLMotionProps<'div'>>;

export interface WebformRadiosWithImageProps extends Record<string, any> {
  entity: ImmutableEntity<Type>;
  touched: Record<string, boolean>,
  errors: Record<string, string>,
  values: Record<string, string>,
}

const WebformRadiosWithImage: React.FC<WebformRadiosWithImageProps> = (props) => {
  const {
    entity,
    touched,
    errors,
    values,
    ...fieldProps
  } = props;

  const { t } = useTranslation();
  const { updateCurrentElement } = useWebformContext();

  const component = entity.toJS();

  const key = component['#webform_key'];
  const title = component['#title'];
  const required = component['#required'] || false;
  const description = component['#description'];
  const helpTitle = component['#help_title'];
  const helpDescription = component['#help'];
  const requiredError = component['#required_error'] || '';
  const layoutOptions = component['#options_display'] || '';
  const elementClassNames = component['#attributes']?.class[0] || '';
  const wrapperClassNames = c('WebformRadiosWithImage', component['#wrapper_attributes']?.class[0], {
    [`WebformRadiosWithImage--${String(layoutOptions)}`]: layoutOptions,
  });

  // Options props.
  const options = component['#options'];
  const images = component['#images'];
  const descriptions = Object.fromEntries(
    Object.entries(images).map(([key_, value_]) => [key_, value_.text]),
  );

  const formattedOptions = useMemo(() => Object.entries(options).map(([value, label]) => {
    const image = images[value];
    if (!image?.source) {
      return label;
    }
    return {
      value,
      label: <Image
        alt={label}
        src={image.source}
        styles={image.styles}
      />,
    };
  }), [images, options]);

  const validation = useCallback((value: string) => {
    let error;
    if (required && !value) {
      error = requiredError || t(['webform.components.radio.required', 'webform.required', 'webform_required']);
    }
    return error;
  }, [required, requiredError, t]);

  useEffect(() => {
    updateCurrentElement(key);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={wrapperClassNames}>
      <MotionConfig transition={{ duration: 0.3 }}>
        <AnimatePresence initial={false} mode="wait">
          <MotionDiv
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="WebformRadiosWithImage__header">
              <WebformAssessmentCount />

              {(helpTitle || helpDescription) && (
                <div className="Webform__help WebformAssessmentRadios__help">
                  <InformationOverlay
                    overlayKey="Overlay_WebformRadios"
                    title={helpTitle}
                    body={helpDescription}
                    buttonIcon="info_alt"
                    buttonClassName="Button--secondary"
                  />
                </div>
              )}
            </div>

            <FormRadioButtons
              {...fieldProps}
              className={elementClassNames}
              title={title}
              required={required}
              description={description}
              fieldName={key}
              validation={validation}
              touched={touched[key]}
              error={errors[key]}
              value={values[key]}
              options={formattedOptions}
              descriptions={descriptions}
            />
          </MotionDiv>
        </AnimatePresence>
      </MotionConfig>
    </div>
  );
};

export default WebformRadiosWithImage;
